import { ReactElement, useContext, useEffect, useMemo, useState } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
    applyAdminSettingsOnload,
    getPermissions,
    checkSubModulesListPermissions,
    getActiveModule,
    mergePermissions,
    growthbook,
    getClientId,
    getDecryptedUserDetails,
} from "@ts/utils";
import { useGetModulesData } from "@hooks/AllCustomHooks";
import PerformanceOverview from "@pages/performance-overview/PerformanceOverview";
import Rules from "@pages/rules/Rules";
import KeywordAnalytics from "@pages/keyword-analytics/KeywordAnalytics";
import ProductAnalytics from "@pages/product-analytics/ProductAnalytics";
import NegativeKeywords from "@pages/negative-keywords/NegativeKeywords";
import KeywordsRecommendations from "@pages/recommendations/keywords/KeywordsRecommendations";
import CampaignsRecommendations from "@pages/recommendations/campaigns/CampaignsRecommendations";
import History from "@pages/history/History";
import Portfolios from "@pages/performance-overview/Portfolios";
import Campaigns from "@pages/performance-overview/Campaigns";
import AdGroups from "@pages/performance-overview/AdGroups";
import Keywords from "@pages/performance-overview/Keywords";
import Products from "@pages/performance-overview/Products";
import Settings from "@pages/settings/Settings";
import AdminSettings from "@pages/admin-settings/AdminSettings";
import PageNotFound from "@pages/page-not-found/PageNotFound";
import KeywordsFromManual from "@pages/recommendations/keywords/KeywordsFromManual";
import KeywordsFromAuto from "@pages/recommendations/keywords/KeywordsFromAuto";
import RevinitiRecommendations from "@pages/recommendations/keywords/RevinitiRecommendations";
import PlacementRecommendations from "@pages/recommendations/campaigns/PlacementRecommendations";
import BudgetRecommendations from "@pages/recommendations/campaigns/BudgetRecommendations";
import Integrations from "@pages/integrations/Integrations";
import Reports from "@pages/reports/ReportsComponent";
import { updateShowAdminSettingsDisplay } from "@features/modalSlice";
import SuggestedNegativeKeywords from "@pages/negative-keywords/SuggestedNegativeKeywords";
import ExistingNegativeKeywords from "@pages/negative-keywords/ExistingNegativeKeywords";
import ReviewAnalyticsBrand from "@pages/reviews/ReviewAnalyticsBrand";
import ReviewAnalyticsSku from "@pages/reviews/ReviewAnalyticsSku";
import ProductDeepdive from "@pages/product-deepdive/ProductDeepdive";
import * as Sentry from "@sentry/react";
import {
    TActiveClientId,
    AppDispatch,
    IAdminToggleBtn,
    ParentModule,
    SubModule,
    TReportsData,
    Platforms,
    IClientAccess,
    IAccess,
    IMainContext,
    TUserdata,
    AppRoutes,
    IClient,
    RevstackDashboardModules,
    ICampaignCreationDisplay,
} from "@ts/common-types";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import Unauthorized from "@pages/unauthorized/Unauthorized";
import {
    cerbosCanListAndCommonPermissions,
    cerbosResourcesConfig,
    mainContext,
    UserRoles,
} from "./ts/constants";
import MeasurementHomePage from "./pages/measurement-home-page/MeasurementHomePage";
import ClientView from "@/pages/admin-settings/components/ClientView";
import UserView from "@/pages/admin-settings/components/UserView";
import MediaPlanning from "./pages/media-planning/MediaPlanning";
import useSentryCrashReport from "./hooks/useSentryCrashReport";
import store from "./store";
import RevstackDashboards from "./pages/revstack-dashboards/RevstackDashboards";
import { setUserDetails } from "./ts/retrieve-data";
import { updateUserData } from "./features/userDataSlice";
import OneDer from "./pages/chatbot/OneDer";
// import Chatbot from "./pages/chatbot/Chatbot";
import Placements from "./pages/performance-overview/Placements";
import CampaignCreation from "./pages/campaign-creation/CampaignCreation";

interface IChildRoute {
    path: string;
    element: ReactElement;
    condition: boolean;
}

const App = Sentry.withProfiler(() => {
    const { cerbosPermissions, setCerbosPermissions } =
        useContext<IMainContext>(mainContext);
    const clients = useSelector(
        (state: { clients: { value: IClient[] } }) => state.clients.value
    );

    const activeClientId: number = getClientId() || clients[0].id;

    const enableRevstack = useSelector(
        (state: IAdminToggleBtn) => state.enableRevstack.value
    );
    const enableKeywordRecommendation = useSelector(
        (state: IAdminToggleBtn) => state.enableKeywordRecommendation.value
    );
    const reportsData = useSelector(
        (state: TReportsData) => state.reportsData.value
    );

    const routesData = useGetModulesData({ type: "routes" });
    const dispatch = useDispatch<AppDispatch>();
    const userData = useSelector((state: TUserdata) => state.userData.value);
    const campaignCreationDisplay = useSelector(
        (state: ICampaignCreationDisplay) => state.campaignCreationDisplay.value
    );

    // Check for staging access restriction
    const [hasStagingAccess, setHasStagingAccess] = useState<boolean>(true);

    // Check if user has staging access via feature flag
    const hasStagingAccessFeatureFlag =
        useFeatureIsOn("HAS_STAGING_ACCESS") || false;

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        //! url params from iot app
        const showAdminSettingsDisplay = urlParams.get(
            "showAdminSettingsDisplay"
        );
        dispatch(
            updateShowAdminSettingsDisplay(showAdminSettingsDisplay === "true")
        );
        if (activeClientId) {
            applyAdminSettingsOnload(activeClientId);
        }
    }, [activeClientId, dispatch]);

    // Check staging access based on feature flag
    useEffect(() => {
        const checkStagingAccess = () => {
            // Only check if we're in staging environment
            // TODO: Change to "staging" for production deployment
            if (process.env.VITE_NODE_ENV !== "staging") {
                setHasStagingAccess(true);
                return;
            }

            // If no userData yet, wait for it to load
            if (!userData) {
                return;
            }

            // Check if user has staging access via feature flag
            // Super admins always have access
            const hasAccess =
                userData.is_super_admin || hasStagingAccessFeatureFlag;

            setHasStagingAccess(hasAccess);
        };

        checkStagingAccess();
    }, [userData, hasStagingAccessFeatureFlag]);

    // Periodic user data refresh every 5 minutes
    useEffect(() => {
        const refreshUserData = async () => {
            try {
                await setUserDetails();
                const refreshedUserData = getDecryptedUserDetails();
                dispatch(updateUserData(refreshedUserData));
            } catch (error) {
                console.error("Failed to refresh user data:", error);
            }
        };

        const interval = setInterval(refreshUserData, 5 * 60 * 1000);

        return () => clearInterval(interval);
    }, [dispatch]);

    const showNewSideBarFeature =
        useFeatureIsOn("NEW_SIDEBAR_FEATURE") || false;

    const showReviewAnalyticsFeature =
        useFeatureIsOn("REVSTACK_FEATURE") || false;

    const showProductAnalysisFeature =
        useFeatureIsOn("PRODUCT_ANALYSIS_FEATURE") || false;

    const showClientsFeature = useFeatureIsOn("CLIENTS_FEATURE") || false;

    const showMediaPlanningFeature =
        useFeatureIsOn("MEDIA_PLANNING_FEATURE") || false;

    const showChatbotFeature = useFeatureIsOn("ONEDER_FEATURE") || false;

    const userAccess = userData?.is_super_admin
        ? {
              role_slug: UserRoles.ADMIN,
              platforms: [
                  Platforms.ACTIVATION,
                  Platforms.CHATBOT,
                  Platforms.MEASUREMENT,
                  Platforms.PLANNING,
              ],
              clients: [{ client_id: activeClientId }],
          }
        : userData?.access?.find((access: IAccess) =>
              access.clients.find(
                  (client: IClientAccess) => client.client_id === activeClientId
              )
          );

    const userRole: string = userData?.is_super_admin
        ? UserRoles.ADMIN
        : userAccess?.role_slug ?? "user";

    useEffect(() => {
        const clientGroup = store.getState().activeClientGroupId.value;
        const clientSubgroupId = store.getState().clientSubGroupId.value;
        const channel = store.getState().channel.value;

        // set growthBook attributes
        growthbook.setAttributes({
            userId: userData.user_id,
            email: userData.email,
            firstName: userData.first_name,
            lastName: userData.last_name,
            role: userRole,
            clientId: activeClientId,
            clientGroup: clientGroup,
            clientSubGroup: clientSubgroupId,
            channel: channel,
        });
    }, [activeClientId, userData, userRole]);

    const routePermissions = useMemo(() => {
        const permissions = checkSubModulesListPermissions(cerbosPermissions);
        return permissions;
    }, [cerbosPermissions]);

    const routes = useMemo(() => {
        const platformAccess = userAccess
            ? userAccess?.platforms.map((item: string) => item.toLowerCase())
            : [];

        const userHasActivationAccess = platformAccess.includes(
            Platforms.ACTIVATION
        );

        const showChatbotSection =
            platformAccess.includes(Platforms.CHATBOT) && showChatbotFeature;

        const routeView = (
            route: ParentModule | SubModule,
            platform: Platforms
        ) => {
            return (
                routesData.includes(route) &&
                platformAccess.includes(platform) &&
                !routePermissions.includes(route)
            );
        };

        const getHomeElement = () => {
            if (!userData || !userAccess) {
                return <Unauthorized />;
            }

            if (
                platformAccess.length === 1 &&
                platformAccess.includes(Platforms.PLANNING)
            ) {
                return <Reports />;
            }

            if (
                platformAccess.length === 1 &&
                platformAccess.includes(Platforms.MEASUREMENT)
            ) {
                return <MeasurementHomePage />;
            }

            if (
                userHasActivationAccess &&
                routesData.includes(ParentModule.PERFORMANCE_OVERVIEW)
            ) {
                return campaignCreationDisplay ? (
                    <CampaignCreation />
                ) : (
                    <PerformanceOverview />
                );
            }

            return <Reports />;
        };

        return [
            {
                path: "/",
                element: getHomeElement(),
                condition: true,
            },
            // {
            //     path: `/${ParentModule.CHATBOT}`,
            //     element: <Chatbot />,
            //     condition: showChatbotSection,
            // },
            {
                path: `/${ParentModule.ONEDER}`,
                element: <OneDer />,
                condition: showChatbotSection,
            },
            // ToDo: Rework this logic (temp-fix for 404 while reloading revstack)
            {
                path: "*",
                element: <PageNotFound />,
                condition: true,
            },
            {
                path: `/${AppRoutes.PAGENOTFOUND}`,
                element: <PageNotFound />,
                condition: true,
            },
            {
                path: `/${Platforms.ACTIVATION}/${ParentModule.PERFORMANCE_OVERVIEW}`,
                element: campaignCreationDisplay ? (
                    <CampaignCreation />
                ) : (
                    <PerformanceOverview />
                ),
                children: [
                    {
                        path: `${SubModule.PORTFOLIO}`,
                        element: <Portfolios />,
                        condition: routeView(
                            SubModule.PORTFOLIO,
                            Platforms.ACTIVATION
                        ),
                    },
                    {
                        path: `${SubModule.CAMPAIGN}`,
                        element: <Campaigns />,
                        condition: routeView(
                            SubModule.CAMPAIGN,
                            Platforms.ACTIVATION
                        ),
                    },
                    {
                        path: `${SubModule.ADGROUP}`,
                        element: <AdGroups />,
                        condition: routeView(
                            SubModule.ADGROUP,
                            Platforms.ACTIVATION
                        ),
                    },
                    {
                        path: `${SubModule.KEYWORD}`,
                        element: <Keywords />,
                        condition: routeView(
                            SubModule.KEYWORD,
                            Platforms.ACTIVATION
                        ),
                    },
                    {
                        path: `${SubModule.PLACEMENTS}`,
                        element: <Placements />,
                        condition: routeView(
                            SubModule.PLACEMENTS,
                            Platforms.ACTIVATION
                        ),
                    },
                    {
                        path: `${SubModule.PRODUCT}`,
                        element: <Products />,
                        condition: routeView(
                            SubModule.PRODUCT,
                            Platforms.ACTIVATION
                        ),
                    },
                ],
                condition: routeView(
                    ParentModule.PERFORMANCE_OVERVIEW,
                    Platforms.ACTIVATION
                ),
            },
            {
                path: `/${Platforms.ACTIVATION}/${ParentModule.RULES}`,
                element: <Rules />,
                condition: routeView(ParentModule.RULES, Platforms.ACTIVATION),
            },
            {
                path: `/${Platforms.ACTIVATION}/${ParentModule.KEYWORD_ANALYTICS}`,
                element: <KeywordAnalytics />,
                condition: routeView(
                    ParentModule.KEYWORD_ANALYTICS,
                    Platforms.ACTIVATION
                ),
            },
            {
                path: `/${Platforms.ACTIVATION}/${ParentModule.PRODUCT_ANALYTICS}`,
                element: <ProductAnalytics />,
                condition: routeView(
                    ParentModule.PRODUCT_ANALYTICS,
                    Platforms.ACTIVATION
                ),
            },
            {
                path: `/${Platforms.ACTIVATION}/${ParentModule.PRODUCT_DEEPDIVE}`,
                element: <ProductDeepdive />,
                condition: routeView(
                    ParentModule.PRODUCT_DEEPDIVE,
                    Platforms.ACTIVATION
                ),
            },
            {
                path: `/${Platforms.ACTIVATION}/${ParentModule.NEGATIVE_KEYWORDS}`,
                element: <NegativeKeywords />,
                condition: routeView(
                    ParentModule.NEGATIVE_KEYWORDS,
                    Platforms.ACTIVATION
                ),
                children: [
                    {
                        path: `${SubModule.SUGGESTED_NEG_KWS}`,
                        element: <SuggestedNegativeKeywords />,
                    },
                    {
                        path: `${SubModule.EXISTING_NEG_KWS}`,
                        element: <ExistingNegativeKeywords />,
                    },
                ],
            },
            {
                path: `/${Platforms.ACTIVATION}/${ParentModule.KEYWORD_RECOMMENDATIONS}`,
                element: <KeywordsRecommendations />,
                condition:
                    routeView(
                        ParentModule.KEYWORD_RECOMMENDATIONS,
                        Platforms.ACTIVATION
                    ) && enableKeywordRecommendation,
                children: [
                    {
                        path: `${SubModule.KEYWORD_MANUAL}`,
                        element: <KeywordsFromManual />,
                    },
                    {
                        path: `${SubModule.KEYWORD_AUTO}`,
                        element: <KeywordsFromAuto />,
                    },
                    {
                        path: `${SubModule.KEYWORD_REVINITI}`,
                        element: <RevinitiRecommendations />,
                    },
                ],
            },
            {
                path: `/${Platforms.ACTIVATION}/${ParentModule.CAMPAIGN_RECOMMENDATIONS}`,
                element: <CampaignsRecommendations />,
                condition: routeView(
                    ParentModule.CAMPAIGN_RECOMMENDATIONS,
                    Platforms.ACTIVATION
                ),
                children: [
                    {
                        path: `${SubModule.CAMPAIGN_BUDGET}`,
                        element: <BudgetRecommendations />,
                    },
                    {
                        path: `${SubModule.CAMPAIGN_PLACEMENT}`,
                        element: <PlacementRecommendations />,
                    },
                ],
            },
            /* {
                path: `/${Platforms.PLANNING}/${ParentModule.REVIEW_ANALYTICS}`,
                condition:
                    routeView(
                        ParentModule.REVIEW_ANALYTICS,
                        Platforms.PLANNING
                    ),
                    //  && showReviewAnalyticsFeature,
                children: [
                    {
                        path: `${SubModule.REVIEW_BRAND}`,
                        element: <ReviewAnalyticsBrand />,
                    },
                    {
                        path: `${SubModule.REVIEW_SKU}`,
                        element: <ReviewAnalyticsSku />,
                    },
                    {
                        path: `${SubModule.CATEGORY_INSIGHTS}`,
                        element: <ReviewAnalyticsCategory />,
                    },
                ],
            },*/
            {
                path: `/${Platforms.ACTIVATION}/${ParentModule.CHANGE_LOG}`,
                element: <History />,
                condition: routeView(
                    ParentModule.CHANGE_LOG,
                    Platforms.ACTIVATION
                ),
            },
            {
                path: `/${ParentModule.SETTINGS}/:tab`,
                element: <Settings />,
                condition: true,
            },
            {
                path: `/${Platforms.PLANNING}/${ParentModule.REVSTACK}/:id`,
                element: <Reports />,
                condition:
                    routeView(ParentModule.REVSTACK, Platforms.PLANNING) &&
                    platformAccess.includes(Platforms.PLANNING),
            },
            {
                path: `/${Platforms.PLANNING}/${SubModule.CATEGORY_ANALYSIS}`,
                element: (
                    <RevstackDashboards
                        module={RevstackDashboardModules.CATEGORY_ANALYSIS}
                        showCategoryFilters={true}
                    />
                ),
                condition:
                    routeView(
                        ParentModule.REVIEW_ANALYTICS,
                        Platforms.PLANNING
                    ) && showReviewAnalyticsFeature,
            },
            {
                path: `/${Platforms.PLANNING}/${SubModule.PRODUCT_ANALYSIS}`,
                element: (
                    <RevstackDashboards
                        module={RevstackDashboardModules.PRODUCT_ANALYSIS}
                        showCategoryFilters={true}
                    />
                ),
                condition:
                    routeView(
                        ParentModule.REVIEW_ANALYTICS,
                        Platforms.PLANNING
                    ) && showProductAnalysisFeature,
            },
            {
                path: `/${Platforms.PLANNING}/${SubModule.NAVIGATOR}`,
                element: (
                    <RevstackDashboards
                        module={RevstackDashboardModules.NAVIGATOR}
                        showCategoryFilters={true}
                    />
                ),
                condition:
                    routeView(
                        ParentModule.REVIEW_ANALYTICS,
                        Platforms.PLANNING
                    ) && showReviewAnalyticsFeature,
            },
            {
                path: `/${Platforms.ACTIVATION}/${ParentModule.COMMON_REPORTS}/:id`,
                element: <Reports />,
                condition:
                    routeView(
                        ParentModule.COMMON_REPORTS,
                        Platforms.ACTIVATION
                    ) && reportsData.common.length,
            },
            {
                path: `/${ParentModule.INTEGRATIONS}`,
                element: <Integrations />,
                condition: true,
            },
            {
                path: `/${ParentModule.ADMIN_SETTINGS}/settings/:tab`,
                element: <AdminSettings />,
                condition: !routePermissions.includes(
                    ParentModule.ADMIN_SETTINGS
                ),
            },
            {
                path: `/${ParentModule.ADMIN_SETTINGS}/settings/clients/:clientId`,
                element: <ClientView />,
                condition:
                    !routePermissions.includes(ParentModule.ADMIN_SETTINGS) &&
                    showClientsFeature,
            },
            {
                path: `/${ParentModule.ADMIN_SETTINGS}/settings/users/:userId`,
                element: <UserView />,
                condition: !routePermissions.includes(
                    ParentModule.ADMIN_SETTINGS
                ),
            },
            {
                path: `/${Platforms.ACTIVATION}/${SubModule.MEDIA_PLANNING}`,
                element: <MediaPlanning />,
                condition:
                    !routePermissions.includes(SubModule.MEDIA_PLANNING) &&
                    !routePermissions.includes(Platforms.ACTIVATION) &&
                    showMediaPlanningFeature,
            },
        ];
    }, [
        routesData,
        enableRevstack,
        reportsData,
        enableKeywordRecommendation,
        showNewSideBarFeature,
        userData,
        userAccess,
        routePermissions,
        showReviewAnalyticsFeature,
        showClientsFeature,
        showMediaPlanningFeature,
        campaignCreationDisplay,
        showProductAnalysisFeature,
    ]);

    const cerbosPrincipal = {
        id: userData?.uid,
        roles: [userRole],
    };
    const location = useLocation();

    const module = useMemo(() => getActiveModule(), [location.pathname]);

    useEffect(() => {
        const fetchPermissions = async () => {
            const currentModule =
                module.activeModule || module.activeParentModule;
            const getCerbosResources =
                cerbosResourcesConfig[currentModule] || [];
            const cerbosResources = mergePermissions(
                cerbosCanListAndCommonPermissions,
                getCerbosResources
            );
            const permissions = await getPermissions(
                cerbosPrincipal,
                cerbosResources
            );
            if (permissions.results.length > 0) {
                setCerbosPermissions(permissions);
            }
        };
        if (userData.uid) {
            fetchPermissions();
        }
    }, [activeClientId, module.activeModule, module.activeParentModule]);

    const errorStatusCode = useSelector(
        (state: any) => state.errorStatusCode.value
    );
    useSentryCrashReport(errorStatusCode, userData);

    useEffect(() => {
        const clientGroup = store.getState().activeClientGroupId.value;
        const clientSubGroup = store.getState().clientSubGroupId.value;

        const chatbotAttributes = sessionStorage.getItem("chatbot");
        sessionStorage.setItem(
            "chatbot",
            JSON.stringify({
                ...(chatbotAttributes ? JSON.parse(chatbotAttributes) : {}),
                client_id: activeClientId,
                client_group_id: clientGroup,
                client_subgroup_id: clientSubGroup,
            })
        );
    }, [clients, activeClientId]);

    // If user doesn't have staging access, show restriction page
    // TODO: Change to === "staging" for production deployment
    if (!hasStagingAccess && process.env.VITE_NODE_ENV === "staging") {
        return <Unauthorized isStagingRestriction={true} />;
    }

    return (
        <>
            {cerbosPermissions.results.length ? (
                <Routes>
                    {routes.map((route, index) => {
                        const { path, element, children, condition } = route;

                        if (!condition) {
                            return null;
                        }

                        if (children) {
                            return (
                                <Route
                                    key={index}
                                    path={path}
                                    element={element}
                                >
                                    {(children as Array<IChildRoute>).map(
                                        (childRoute, childIndex) => {
                                            if (
                                                childRoute.condition === false
                                            ) {
                                                return null; // Skip rendering this child route
                                            }
                                            return (
                                                <Route
                                                    key={childIndex}
                                                    path={childRoute.path}
                                                    element={childRoute.element}
                                                />
                                            );
                                        }
                                    )}
                                </Route>
                            );
                        }

                        return (
                            <Route key={index} path={path} element={element} />
                        );
                    })}
                </Routes>
            ) : (
                ""
            )}
        </>
    );
});

export default App;
